import {
    KboContextSectionStatus,
    KboExploration,
    KboExplorationNode,
    KboNodeFinding,
    KboAgentInvocation,
    KboGenerationHistory,
    KboMultiLevelAnalysisResult,
    KboNodeSelectedFinding,
    KboPhaseRecord,
    KboReport,
    KboReportFindingContent,
    KboReportExplorationPathStep,
    KboResearchQuestion,
    KboFlowRunOutput,
} from "../KboFlowDebug/KboFlowRunDebug";

export type KboGroundingSignal = {
    kind?: string;
    name?: string;
    volume?: number;
    changePercent?: number;
    eddyRate?: number;
};

export type DigestQuestion = KboResearchQuestion & {
    groundingSignals?: KboGroundingSignal[];
};

export type DigestReport = KboReport & {
    analysisRunId?: string;
    questionId?: string;
};

export type DigestContext = {
    identity?: Record<string, unknown>;
    tenantProfile?: Record<string, unknown>;
    userProfile?: Record<string, unknown>;
    permissions?: Record<string, unknown>;
    classifierCatalog?: Record<string, unknown>;
    signalSnapshot?: Record<string, unknown>;
    hierarchies?: Record<string, unknown>;
    generationHistory?: KboGenerationHistory;
    sectionStatuses?: KboContextSectionStatus[];
    sources?: Array<Record<string, unknown>>;
};

export type DigestProgramEvent = {
    id?: string;
    startDate?: string;
    description: string;
};

export function asRecord(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === "object"
        ? (value as Record<string, unknown>)
        : undefined;
}

export function asString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value : undefined;
}

export function asNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value)
        ? value
        : undefined;
}

export function asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
}

export function getDigestQuestions(output: KboFlowRunOutput): DigestQuestion[] {
    const generated = (output.generatedQuestions ?? []) as DigestQuestion[];
    const validated = (output.validatedQuestions ?? []) as DigestQuestion[];
    if (validated.length === 0) return generated;

    const generatedById = new Map(
        generated.map((question) => [question.id, question]),
    );
    return validated.map((question, index) => {
        const generatedQuestion =
            (question.id ? generatedById.get(question.id) : undefined) ??
            generated[index];
        return {
            ...generatedQuestion,
            ...question,
            groundingSignals:
                question.groundingSignals ??
                generatedQuestion?.groundingSignals ??
                [],
        };
    });
}

export function getPursuedQuestionIds(output: KboFlowRunOutput): Set<string> {
    return new Set(
        (output.pursuedQuestions ?? [])
            .map((question) => question.id)
            .filter((id): id is string => !!id),
    );
}

export function getQuestionAnalysis(
    output: KboFlowRunOutput,
    question: DigestQuestion,
): KboMultiLevelAnalysisResult | undefined {
    return (output.analysisResults ?? []).find(
        (analysis) =>
            analysis.question?.id === question.id ||
            analysis.question?.text === question.text,
    );
}

/** The v5 exploration tree, when the run recorded one with at least one node. */
export function getExploration(
    output: KboFlowRunOutput,
): KboExploration | undefined {
    const exploration = output.exploration;
    return exploration?.nodes && exploration.nodes.length > 0
        ? exploration
        : undefined;
}

/**
 * The exploration tree for a specific question. The exploration is rooted at a single
 * `rootQuestionId`; return it only when one of its nodes is rooted at this question so
 * the fan-out renders under the question KBo actually pursued.
 */
export function getExplorationForQuestion(
    output: KboFlowRunOutput,
    questionId?: string,
): KboExploration | undefined {
    const exploration = getExploration(output);
    if (!exploration) return undefined;
    if (!questionId) return exploration;
    const rooted = (exploration.nodes ?? []).some(
        (node) => node.rootQuestionId === questionId,
    );
    return rooted ? exploration : undefined;
}

/** A v6/v7 email finding resolved back to the exact nodes and results that produced it. */
export type V7FindingPathStep = {
    path: KboReportExplorationPathStep;
    node?: KboExplorationNode;
    selected?: KboNodeSelectedFinding;
};

export type V7FindingPath = {
    finding: KboReportFindingContent;
    report: KboReport;
    steps: V7FindingPathStep[];
};

export function getV7ReportFindings(output: KboFlowRunOutput): Array<{
    finding: KboReportFindingContent;
    report: KboReport;
    reportIndex: number;
}> {
    return (output.reports ?? []).flatMap((report, reportIndex) =>
        (report.data?.findings ?? []).map((finding) => ({
            finding,
            report,
            reportIndex,
        })),
    );
}

function selectedResultId(
    selected: KboNodeSelectedFinding,
): string | undefined {
    return selected.result?.resultId ?? selected.finding?.resultId;
}

export function getV7FindingPath(
    output: KboFlowRunOutput,
    report: KboReport,
    finding: KboReportFindingContent,
): V7FindingPath {
    const nodesById = new Map(
        (getExploration(output)?.nodes ?? []).map((node) => [
            node.nodeId,
            node,
        ]),
    );
    return {
        finding,
        report,
        steps: (finding.explorationPath ?? []).map((path) => {
            const node = path.explorationNodeId
                ? nodesById.get(path.explorationNodeId)
                : undefined;
            const selected = (node?.analysis?.selected ?? []).find(
                (candidate) => selectedResultId(candidate) === path.resultId,
            );
            return { path, node, selected };
        }),
    };
}

export type HowKboGotHereEntryKind =
    | "cohort"
    | "dimension"
    | "segment"
    | "finding";

/** A product-facing fact in the root-to-leaf path for one delivered finding. */
export type HowKboGotHereEntry = {
    kind: HowKboGotHereEntryKind;
    label: string;
    title: string;
    detail?: string;
    count?: number;
    denominator?: number;
    share?: number;
    rationale?: string;
    skills: string[];
    resultId?: string;
    /** The exploration node this fact belongs to; set for every entry kind. */
    nodeId?: string;
    /** True when the persisted result retains which conversations matched it. */
    hasMatchingConversationIds?: boolean;
    peerResults?: V7AnalysisPeerResult[];
};

/** A sibling result produced from the same analyzed dimension value. */
export type V7AnalysisPeerResult = {
    result: KboNodeFinding;
    resultId: string;
    count?: number;
    denominator?: number;
    share?: number;
    hasMatchingConversationIds: boolean;
};

function shareOf(
    numerator?: number,
    denominator?: number,
): number | undefined {
    return typeof numerator === "number" &&
        typeof denominator === "number" &&
        denominator > 0
        ? numerator / denominator
        : undefined;
}

/** Returns the first retained denominator that can describe this numerator. */
function denominatorFor(
    numerator: number | undefined,
    ...candidates: Array<number | undefined>
): number | undefined {
    return candidates.find(
        (candidate): candidate is number =>
            typeof candidate === "number" &&
            (typeof numerator !== "number" || candidate >= numerator),
    );
}

function cohortCountForPathStep(item: V7FindingPathStep): number | undefined {
    return (
        item.path.analyzedCohortCount ??
        item.node?.analysis?.analyzedCohortCount ??
        item.node?.focus?.startCohortCount
    );
}

function analysisResultForPathStep(item: V7FindingPathStep) {
    return analysisResultForResultId(item.node, item.path.resultId);
}

function analysisResultForResultId(
    node: KboExplorationNode | undefined,
    resultId?: string,
) {
    if (!resultId) return undefined;
    return (node?.analysis?.results ?? []).find((analysisResult) =>
        [...(analysisResult.results ?? []), ...(analysisResult.findings ?? [])].some(
            (result) => result.resultId === resultId,
        ),
    );
}

function resultsForAnalysisResult(
    analysisResult: ReturnType<typeof analysisResultForResultId>,
): KboNodeFinding[] {
    return [
        ...(analysisResult?.results ?? []),
        ...(analysisResult?.findings ?? []),
    ];
}

function metricsForResult(
    result: KboNodeFinding | undefined,
    cohortCount?: number,
) {
    const count = result?.conversationCount;
    const denominator = denominatorFor(
        count,
        result?.totalConversationsReviewed,
        cohortCount,
    );
    return {
        count,
        denominator,
        share: result?.conversationShare ?? shareOf(count, denominator),
    };
}

/** Labels the material KBo actually supplied to the analysis model. */
export function analysisMaterialLabel(materialSource?: string): string {
    switch (materialSource?.replace(/[^a-z]/gi, "").toLowerCase()) {
        case "conversationsummary":
            return "Analyzed Summaries";
        case "modelreasoning":
            return "Analyzed Reasoning";
        default:
            return "Measured";
    }
}

/**
 * Turns a retained v7 report path into the concise, repeated product-facing facts
 * shown under a delivered finding. Values come only from the report path/node data;
 * unavailable counts and percentages stay absent rather than being inferred.
 */
export function getHowKboGotHereEntries(
    output: KboFlowRunOutput,
    report: KboReport,
    finding: KboReportFindingContent,
): HowKboGotHereEntry[] {
    return getHowKboGotHereEntriesForPath(
        getV7FindingPath(output, report, finding).steps,
    );
}

/** Turns a retained root-to-leaf path into the product-facing provenance facts. */
export function getHowKboGotHereEntriesForPath(
    steps: V7FindingPathStep[],
): HowKboGotHereEntry[] {
    return steps.flatMap((item) => {
        const node = item.node;
        const analysis = node?.analysis;
        const analysisResult = analysisResultForPathStep(item);
        const storedResult = resultsForAnalysisResult(analysisResult).find(
            (result) => result.resultId === item.path.resultId,
        );
        const result =
            item.selected?.result ?? item.selected?.finding ?? storedResult;
        const cohortCount = cohortCountForPathStep(item);
        const distribution = asRecord(analysisResult?.distribution);
        const segmentCount =
            item.path.segmentConversationCount ?? asNumber(distribution?.count);
        const resultMetrics = metricsForResult(result, cohortCount);
        // `finding.denominator` belongs to the presentation/report layer and can be
        // a reviewed-material count rather than this node's cohort. Never use it in
        // the provenance metric — the result's retained denominator and node cohort
        // describe the numerator shown here.
        const segmentShare =
            shareOf(segmentCount, cohortCount) ?? asNumber(distribution?.share);
        const skills = getInvocationSkills(node?.invocations);
        const dimension = item.path.dimension ?? analysis?.dimension?.alias;
        const segment = item.path.segment;
        const peerResults = resultsForAnalysisResult(analysisResult).reduce<
            V7AnalysisPeerResult[]
        >((peers, candidate) => {
            if (
                !candidate.resultId ||
                candidate.resultId === item.path.resultId ||
                peers.some((peer) => peer.resultId === candidate.resultId)
            ) {
                return peers;
            }
            return [
                ...peers,
                {
                    result: candidate,
                    resultId: candidate.resultId,
                    hasMatchingConversationIds:
                        candidate.matchingConversationIds !== undefined,
                    ...metricsForResult(candidate, cohortCount),
                },
            ];
        }, []);
        const hasMatchingConversationIds =
            item.selected?.result?.matchingConversationIds !== undefined ||
            item.selected?.finding?.matchingConversationIds !== undefined;

        return [
            {
                kind: "cohort",
                label: "Started with",
                title:
                    typeof cohortCount === "number"
                        ? "Conversations in this cohort"
                        : "Starting cohort",
                detail: node ? cohortScopeLabel(node) : undefined,
                count: cohortCount,
                skills: [],
                nodeId: node?.nodeId,
            },
            {
                kind: "dimension",
                label: "Split by",
                title: dimension ?? "Dimension not retained",
                rationale: analysis?.dimension?.reason,
                skills,
                nodeId: node?.nodeId,
            },
            {
                kind: "segment",
                label: "Landed on",
                title: segment ?? "Selected segment not retained",
                count: segmentCount,
                share: segmentShare,
                rationale: item.selected?.reason,
                skills: [],
                nodeId: node?.nodeId,
            },
            {
                kind: "finding",
                label: analysisMaterialLabel(analysisResult?.materialSource),
                title: result?.name ?? "Measured result not retained",
                detail: result?.claim,
                count: resultMetrics.count,
                denominator: resultMetrics.denominator,
                share: resultMetrics.share,
                rationale: result?.reasoning,
                skills: [],
                resultId: item.path.resultId,
                nodeId: node?.nodeId,
                hasMatchingConversationIds,
                peerResults,
            },
        ];
    });
}

/** A terminal selected result that was explored but not delivered in this report. */
export type V7ExploredLeafResult = {
    result: KboNodeFinding;
    selected: KboNodeSelectedFinding;
    path: V7FindingPathStep[];
    stopReason?: string;
};

/** A result that was analyzed but was neither delivered nor retained as an explored leaf. */
export type V7AnalysisOnlyResult = {
    result: KboNodeFinding;
    node: KboExplorationNode;
    path: V7FindingPathStep[];
    pursuedDeeper: boolean;
};

function childForSelectedResult(
    nodes: KboExplorationNode[],
    node: KboExplorationNode,
    selected: KboNodeSelectedFinding,
): KboExplorationNode | undefined {
    const resultId = selectedResultId(selected);
    const findingId = selected.finding?.findingId;
    return nodes.find(
        (candidate) =>
            candidate.parentNodeId === node.nodeId &&
            ((!!resultId && candidate.focus?.parentResultId === resultId) ||
                (!!findingId && candidate.focus?.parentFindingId === findingId)),
    );
}

function pathStepForSelectedResult(
    node: KboExplorationNode,
    selected: KboNodeSelectedFinding,
): V7FindingPathStep | undefined {
    const resultId = selectedResultId(selected);
    if (!resultId) return undefined;
    return pathStepForResult(node, resultId, selected);
}

function pathStepForResult(
    node: KboExplorationNode,
    resultId: string,
    selected?: KboNodeSelectedFinding,
): V7FindingPathStep {
    const analysisResult = analysisResultForResultId(node, resultId);
    const distribution = asRecord(analysisResult?.distribution);
    return {
        path: {
            explorationNodeId: node.nodeId,
            parentNodeId: node.parentNodeId,
            passId: node.analysis?.passId,
            resultId,
            dimension: node.analysis?.dimension?.alias,
            segment: asString(distribution?.value),
            analyzedCohortCount: cohortCountForPathStep({ path: {}, node }),
            segmentConversationCount: asNumber(distribution?.count),
        },
        node,
        selected,
    };
}

/**
 * Finds terminal selected results under this report's root that were not included
 * in that report's email. Unselected candidates are intentionally omitted because
 * they have no durable selection/stopping rationale to explain to product users.
 */
export function getV7ExploredLeafResults(
    output: KboFlowRunOutput,
    report: KboReport,
): V7ExploredLeafResult[] {
    const root = getV7RootNode(output, report);
    if (!root) return [];
    const nodes = getExploration(output)?.nodes ?? [];
    const delivered = new Set(
        getV7ReportFindings(output)
            .filter((item) => item.report === report)
            .map((item) => item.finding.leafAnalysisResultId)
            .filter((id): id is string => !!id),
    );
    const leaves: V7ExploredLeafResult[] = [];
    const seenResultIds = new Set<string>();

    const visit = (
        node: KboExplorationNode,
        ancestors: V7FindingPathStep[],
        visitedNodeIds: Set<string>,
    ) => {
        (node.analysis?.selected ?? []).forEach((selected) => {
            const resultId = selectedResultId(selected);
            const result = selected.result ?? selected.finding;
            const step = pathStepForSelectedResult(node, selected);
            if (!resultId || !result || !step) return;
            const path = [...ancestors, step];
            const child = childForSelectedResult(nodes, node, selected);
            if (child && child.nodeId && !visitedNodeIds.has(child.nodeId)) {
                visit(child, path, new Set([...visitedNodeIds, child.nodeId]));
                return;
            }
            if (delivered.has(resultId) || seenResultIds.has(resultId)) return;
            seenResultIds.add(resultId);
            leaves.push({
                result,
                selected,
                path,
                stopReason: selected.reason,
            });
        });
    };

    visit(root, [], new Set(root.nodeId ? [root.nodeId] : []));
    return leaves;
}

/**
 * Returns every report-scoped v7 result that has no rendered outcome card. These results are
 * still useful provenance: most were not pursued further, while a retained parent result can
 * explicitly show that KBo continued deeper from it.
 */
export function getV7AnalysisOnlyResults(
    output: KboFlowRunOutput,
    report: KboReport,
): V7AnalysisOnlyResult[] {
    const root = getV7RootNode(output, report);
    if (!root) return [];

    const nodes = getExploration(output)?.nodes ?? [];
    const scopedNodes: Array<{
        node: KboExplorationNode;
        ancestors: V7FindingPathStep[];
    }> = [];
    const visitedNodeIds = new Set<string>();
    const visit = (
        node: KboExplorationNode,
        ancestors: V7FindingPathStep[],
    ) => {
        if (node.nodeId && visitedNodeIds.has(node.nodeId)) return;
        if (node.nodeId) visitedNodeIds.add(node.nodeId);
        scopedNodes.push({ node, ancestors });
        if (!node.nodeId) return;
        nodes
            .filter((candidate) => candidate.parentNodeId === node.nodeId)
            .forEach((child) => {
                const parentResultId = child.focus?.parentResultId;
                const selected = (node.analysis?.selected ?? []).find(
                    (candidate) => selectedResultId(candidate) === parentResultId,
                );
                const nextAncestors = parentResultId
                    ? [
                          ...ancestors,
                          pathStepForResult(node, parentResultId, selected),
                      ]
                    : ancestors;
                visit(child, nextAncestors);
            });
    };
    visit(root, []);

    const deliveredResultIds = new Set(
        getV7ReportFindings(output)
            .filter((item) => item.report === report)
            .map((item) => item.finding.leafAnalysisResultId)
            .filter((id): id is string => !!id),
    );
    const exploredResultIds = new Set(
        getV7ExploredLeafResults(output, report)
            .map((item) => item.result.resultId)
            .filter((id): id is string => !!id),
    );
    const seenResultIds = new Set<string>();

    return scopedNodes
        .flatMap(({ node, ancestors }) =>
            (node.analysis?.results ?? []).flatMap((analysisResult) =>
                resultsForAnalysisResult(analysisResult).flatMap((result) => {
                const resultId = result.resultId;
                if (
                    !resultId ||
                    deliveredResultIds.has(resultId) ||
                    exploredResultIds.has(resultId) ||
                    seenResultIds.has(resultId)
                ) {
                    return [];
                }
                seenResultIds.add(resultId);
                const selected = (node.analysis?.selected ?? []).find(
                    (candidate) => selectedResultId(candidate) === resultId,
                );
                return [
                    {
                        result,
                        node,
                        path: [
                            ...ancestors,
                            pathStepForResult(node, resultId, selected),
                        ],
                        pursuedDeeper: !!(
                            selected && childForSelectedResult(nodes, node, selected)
                        ),
                    },
                ];
                }),
            ),
        )
        .sort((left, right) => (left.node.depth ?? 0) - (right.node.depth ?? 0));
}

/** Prefer the report-scoped root; older v6 records fall back to question/depth. */
export function getV7RootNode(
    output: KboFlowRunOutput,
    report: KboReport,
): KboExplorationNode | undefined {
    const nodes = getExploration(output)?.nodes ?? [];
    if (report.rootExplorationNodeId) {
        const exact = nodes.find(
            (node) => node.nodeId === report.rootExplorationNodeId,
        );
        if (exact) return exact;
    }
    return (
        nodes.find(
            (node) =>
                node.rootQuestionId === report.questionId &&
                (node.depth ?? 0) === 0,
        ) ?? nodes.find((node) => (node.depth ?? 0) === 0)
    );
}

export type V7ExplorationBranch = {
    result: KboNodeSelectedFinding;
    child?: KboExplorationNode;
    segment?: string;
    cohortCount?: number;
    deliveredResultIds: string[];
};

/**
 * The top-level fan-out summary used by the collapsed "other paths" section.
 * A branch can include emailed leaf results, but the email cards remain the only
 * place those outcomes are rendered in full.
 */
export function getV7ExplorationBranches(
    output: KboFlowRunOutput,
    report: KboReport,
): V7ExplorationBranch[] {
    const root = getV7RootNode(output, report);
    if (!root) return [];
    const nodes = getExploration(output)?.nodes ?? [];
    const delivered = getV7ReportFindings(output)
        .filter((item) => item.report === report)
        .map((item) => item.finding.leafAnalysisResultId)
        .filter((id): id is string => !!id);
    return (root.analysis?.selected ?? []).map((result) => {
        const resultId = selectedResultId(result);
        const child = nodes.find(
            (node) =>
                node.focus?.parentResultId === resultId &&
                node.parentNodeId === root.nodeId,
        );
        const distribution = (root.analysis?.results ?? []).find((entry) =>
            entry.results?.some(
                (entryResult) => entryResult.resultId === resultId,
            ),
        );
        const rawDistribution = asRecord(distribution?.distribution);
        const segment = asString(rawDistribution?.value);
        const cohortCount = asNumber(rawDistribution?.count);
        const descendantIds = new Set<string>();
        const visit = (node?: KboExplorationNode) => {
            if (!node) return;
            (node.analysis?.selected ?? []).forEach((selected) => {
                const id = selectedResultId(selected);
                if (id) descendantIds.add(id);
                visit(
                    nodes.find(
                        (candidate) =>
                            candidate.focus?.parentResultId === id &&
                            candidate.parentNodeId === node.nodeId,
                    ),
                );
            });
        };
        if (resultId) descendantIds.add(resultId);
        visit(child);
        return {
            result,
            child,
            segment,
            cohortCount,
            deliveredResultIds: delivered.filter((id) => descendantIds.has(id)),
        };
    });
}

/**
 * One node in the rendered exploration tree: the raw node, its pursued findings (each
 * paired with the child node it spawned, if any), and the findings it chose not to
 * pursue. "Pursued" == `exploreDeeper`; the child is matched by
 * `finding.findingId → child.focus.parentFindingId`.
 */
export type ExplorationTreeNode = {
    node: KboExplorationNode;
    pursued: Array<{
        selected: KboNodeSelectedFinding;
        child?: ExplorationTreeNode;
    }>;
    notPursued: KboNodeSelectedFinding[];
};

export type ExplorationFindingPath = {
    finding: KboNodeFinding;
    selected?: KboNodeSelectedFinding;
    nodes: ExplorationTreeNode[];
};

/** Finds a surfaced finding and the root-to-finding path that produced it. */
export function getExplorationFindingPath(
    output: KboFlowRunOutput,
    findingId?: string,
): ExplorationFindingPath | undefined {
    if (!findingId) return undefined;
    const visit = (
        item: ExplorationTreeNode,
        ancestors: ExplorationTreeNode[],
    ): ExplorationFindingPath | undefined => {
        const path = [...ancestors, item];
        for (const selected of item.pursued) {
            if (selected.selected.finding?.findingId === findingId) {
                return {
                    finding: selected.selected.finding,
                    selected: selected.selected,
                    nodes: path,
                };
            }
            if (selected.child) {
                const match = visit(selected.child, path);
                if (match) return match;
            }
        }
        const unpursued = item.notPursued.find(
            (selected) => selected.finding?.findingId === findingId,
        );
        return unpursued?.finding
            ? { finding: unpursued.finding, selected: unpursued, nodes: path }
            : undefined;
    };
    for (const root of buildExplorationTree(getExploration(output) ?? {})) {
        const match = visit(root, []);
        if (match) return match;
    }
    return undefined;
}

/** Skills are captured by the Agent Skills Provider as `load_skill({ skillName })` tool calls. */
export function getInvocationSkills(
    invocations?: KboAgentInvocation[],
): string[] {
    const names = new Set<string>();
    (invocations ?? []).forEach((invocation) => {
        (invocation.toolCalls ?? []).forEach((toolCall) => {
            if (toolCall.name?.toLowerCase() !== "load_skill") return;
            try {
                const parsed = JSON.parse(toolCall.argumentsJson ?? "{}") as {
                    skillName?: unknown;
                };
                if (
                    typeof parsed.skillName === "string" &&
                    parsed.skillName.trim()
                ) {
                    names.add(parsed.skillName.trim());
                }
            } catch {
                // Older/corrupt traces remain inspectable; they simply have no skill label.
            }
        });
    });
    return [...names];
}

/**
 * Turns the flat node list into the path/fan-out tree. Children link to the finding
 * that spawned them via `focus.parentFindingId`. Guards against cycles and dangling
 * parents so no node is dropped: any node not reached from a root is appended as its
 * own root.
 */
export function buildExplorationTree(
    exploration: KboExploration,
): ExplorationTreeNode[] {
    const nodes = exploration.nodes ?? [];
    const byId = new Map(nodes.map((node) => [node.nodeId, node] as const));
    const childByFindingId = new Map<string, KboExplorationNode>();
    nodes.forEach((node) => {
        const findingId = node.focus?.parentFindingId;
        if (findingId) childByFindingId.set(findingId, node);
    });

    const seen = new Set<string>();
    const build = (node: KboExplorationNode): ExplorationTreeNode => {
        if (node.nodeId) seen.add(node.nodeId);
        const pursued: ExplorationTreeNode["pursued"] = [];
        const notPursued: KboNodeSelectedFinding[] = [];
        (node.analysis?.selected ?? []).forEach((selected) => {
            if (!selected.exploreDeeper) {
                notPursued.push(selected);
                return;
            }
            const findingId = selected.finding?.findingId;
            const candidate = findingId
                ? childByFindingId.get(findingId)
                : undefined;
            const child =
                candidate && !(candidate.nodeId && seen.has(candidate.nodeId))
                    ? build(candidate)
                    : undefined;
            pursued.push({ selected, child });
        });
        return { node, pursued, notPursued };
    };

    const roots = nodes.filter(
        (node) =>
            (node.depth ?? 0) === 0 ||
            !node.parentNodeId ||
            !byId.has(node.parentNodeId),
    );
    const tree = roots
        .filter((node) => !seen.has(node.nodeId ?? ""))
        .map(build);
    // Defensive: surface any node not reached from a root rather than dropping it.
    nodes.forEach((node) => {
        if (!node.nodeId || !seen.has(node.nodeId)) tree.push(build(node));
    });
    return tree;
}

/**
 * A short, human label for the cohort a node analyzed: its filter expression
 * (e.g. "DriverName = Injection Device Issue") or the cohort name / "Full population".
 */
export function cohortScopeLabel(node: KboExplorationNode): string {
    const cohort = asRecord(node.analysis?.analyzedCohort);
    const filters = asArray(cohort?.filters);
    if (filters.length === 0) {
        return asString(cohort?.name) ?? "Full population";
    }
    return filters
        .map((filter) => {
            const record = asRecord(filter);
            const alias = asString(record?.alias);
            const rawValue = record?.value;
            const value =
                asString(rawValue) ??
                (rawValue !== null && rawValue !== undefined
                    ? String(rawValue)
                    : undefined);
            return alias && value ? `${alias} = ${value}` : alias;
        })
        .filter(Boolean)
        .join(" · ");
}

/**
 * The report written for one question. `KboReport` itself carries no question id —
 * the link lives on each `ReportFinding.questionId` — so match on the findings and
 * fall back to the question text. Never guess at `reports[0]`: on a multi-report run
 * that shows one question another question's email and marks it delivered.
 */
export function getQuestionReport(
    output: KboFlowRunOutput,
    question: DigestQuestion,
): DigestReport | undefined {
    const reports = (output.reports ?? []) as DigestReport[];
    const questionId = question.id?.toLowerCase();
    const questionText = question.text?.trim().toLowerCase();
    return reports.find(
        (report) =>
            (!!questionId &&
                (report.questionId?.toLowerCase() === questionId ||
                    (report.findings ?? []).some(
                        (finding) =>
                            finding.questionId?.toLowerCase() === questionId,
                    ))) ||
            (!!questionText &&
                (report.findings ?? []).some(
                    (finding) =>
                        finding.questionText?.trim().toLowerCase() ===
                        questionText,
                )),
    );
}

/**
 * Whether the run as a whole finished, failed, or is still going. The persisted
 * run status is authoritative when present; older rows fall back to their phase
 * records. Individual failed invocations are deliberately not counted — those
 * are retried and reported separately.
 */
export function runOutputStatus(
    output: KboFlowRunOutput,
    persistedStatus?: string | null,
): "completed" | "failed" | "inProgress" {
    switch (persistedStatus?.toLowerCase()) {
        case "completed":
            return "completed";
        case "failed":
            return "failed";
        case "running":
        case "bookmarked":
            return "inProgress";
    }

    const statuses = (output.phaseRecords ?? []).map((phase) =>
        (phase.status ?? "").toLowerCase(),
    );
    if (statuses.some((status) => status.includes("fail"))) return "failed";
    return output.completedAtUtc ? "completed" : "inProgress";
}

export function getPhase(
    output: KboFlowRunOutput,
    ...names: string[]
): KboPhaseRecord | undefined {
    const normalizedNames = names.map((name) => name.toLowerCase());
    return (output.phaseRecords ?? []).find((record) =>
        normalizedNames.includes(record.phase?.toLowerCase() ?? ""),
    );
}

export function getContext(output: KboFlowRunOutput): DigestContext {
    return (output.assembledContext ?? {}) as DigestContext;
}

export function questionStatus(
    output: KboFlowRunOutput,
    question: DigestQuestion,
): "delivered" | "pursued" | "held" | "rejected" {
    const pursued = !!(
        question.id && getPursuedQuestionIds(output).has(question.id)
    );
    if (pursued && getQuestionReport(output, question)) return "delivered";
    if (pursued) return "pursued";
    return question.validation?.accepted === false ? "rejected" : "held";
}

export function shortQuestionLabel(question: DigestQuestion): string {
    const text = question.text?.replace(/[?!.]+$/, "") ?? "Untitled question";
    const withoutLead = text.replace(
        /^(what|how|why|where|when|which|who|are|is|has|have|do|does|did)\s+/i,
        "",
    );
    return withoutLead.length > 29
        ? `${withoutLead.slice(0, 27).trim()}…`
        : withoutLead;
}

export function formatRunDate(value?: string | null): string {
    if (!value) return "Unknown date";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }).format(date);
}

export function formatCompactDate(value?: string | null): string {
    if (!value) return "Unknown date";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(date);
}

export function formatPercent(value?: number | null, digits = 1): string {
    return typeof value === "number"
        ? `${(value * 100).toFixed(digits)}%`
        : "—";
}

/** Number of whole days between two ISO timestamps, or undefined if either is missing/invalid. */
export function daysBetween(
    start?: string | null,
    end?: string | null,
): number | undefined {
    if (!start || !end) return undefined;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (Number.isNaN(ms) || ms < 0) return undefined;
    return Math.max(1, Math.round(ms / 86_400_000));
}

/** Short badge text for a signal's period-over-period change (e.g. "Flat", "Up 12%", "Down 8%"). */
export function volumeChangeWord(changePercent?: number): string {
    if (typeof changePercent !== "number") return "—";
    if (Math.abs(changePercent) < 5) return "Flat";
    const direction = changePercent > 0 ? "Up" : "Down";
    return `${direction} ${Math.abs(changePercent).toFixed(0)}%`;
}

export function totalModelCalls(output: KboFlowRunOutput): number {
    return (output.phaseRecords ?? []).reduce(
        (total, phase) => total + (phase.invocations?.length ?? 0),
        0,
    );
}

const CONTEXT_SECTION_TITLES: Record<string, string> = {
    tenantprofile: "Tenant profile",
    userprofile: "User profile",
    permissions: "Permissions",
    signalsnapshot: "Signal snapshot",
    classifiercatalog: "Classifier catalog",
    modelcatalog: "Classifier catalog",
    hierarchies: "Hierarchies",
    generationhistory: "Generation history",
};

/** Human title for a raw `sectionStatuses[].section` key (e.g. "TenantProfile" -> "Tenant profile"). */
export function contextSectionTitle(section?: string): string {
    const key = section?.toLowerCase().replace(/[^a-z]/g, "");
    return (key && CONTEXT_SECTION_TITLES[key]) || section || "Section";
}

/** Parses a .NET TimeSpan string ("00:03:04.0655420") or ISO-8601 duration ("PT3M4S") into total seconds. */
export function parseDurationSeconds(
    value?: string | null,
): number | undefined {
    if (!value) return undefined;
    const isoMatch =
        /^P(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(
            value,
        );
    if (isoMatch && (isoMatch[1] || isoMatch[2] || isoMatch[3])) {
        const hours = Number(isoMatch[1] ?? 0);
        const minutes = Number(isoMatch[2] ?? 0);
        const seconds = Number(isoMatch[3] ?? 0);
        return hours * 3600 + minutes * 60 + seconds;
    }
    const clockMatch = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/.exec(
        value,
    );
    if (clockMatch) {
        const days = Number(clockMatch[1] ?? 0);
        const hours = Number(clockMatch[2]);
        const minutes = Number(clockMatch[3]);
        const seconds = Number(clockMatch[4]);
        return days * 86400 + hours * 3600 + minutes * 60 + seconds;
    }
    return undefined;
}

/** Formats a TimeSpan/ISO-8601 duration string as "m:ss", matching call-duration conventions. */
export function formatClockDuration(value?: string | null): string {
    const totalSeconds = parseDurationSeconds(value);
    if (totalSeconds === undefined) return "—";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Program events whose start date falls within the run's conversation window. */
export function getProgramEventsInWindow(
    context: DigestContext,
): DigestProgramEvent[] {
    const tenantContext = asRecord(context.tenantProfile?.context);
    const events = asArray(tenantContext?.programEvents)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row);
    const startUtc = asString(context.identity?.startDateUtc);
    const endUtc = asString(context.identity?.endDateUtc);
    const windowStart = startUtc ? new Date(startUtc).getTime() : undefined;
    const windowEnd = endUtc ? new Date(endUtc).getTime() : undefined;
    return events
        .map((row) => ({
            id: asString(row.id),
            startDate: asString(row.startDate),
            description: asString(row.description) ?? "Untitled event",
        }))
        .filter((event) => {
            if (
                !event.startDate ||
                windowStart === undefined ||
                windowEnd === undefined
            ) {
                return true;
            }
            const eventTime = new Date(event.startDate).getTime();
            return (
                !Number.isNaN(eventTime) &&
                eventTime >= windowStart &&
                eventTime <= windowEnd
            );
        })
        .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""));
}

export type DigestTextItem = { id?: string; text: string };

/** A {id,text}[] field on a context blob (e.g. userProfile.context.goals/responsibilities/successMetrics/currentConcerns). */
export function getTextItems(
    contextObj: Record<string, unknown> | undefined,
    key: string,
): DigestTextItem[] {
    return asArray(contextObj?.[key])
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map(
            (row): DigestTextItem => ({
                id: asString(row.id),
                text: asString(row.text) ?? "",
            }),
        )
        .filter((item) => !!item.text);
}

export type DigestBreakdownRow = {
    name: string;
    count: number;
    eddyCount?: number;
    eddyRate?: number;
    changePercent?: number;
};

/** Any `signalSnapshot[key]` breakdown shaped like `{category,count,eddyCount,eddyRate,changePercent}[]` — topics, themes, classifiers, contactTypes, journeyStages. */
export function getBreakdownRows(
    signalSnapshot: Record<string, unknown> | undefined,
    key: string,
): DigestBreakdownRow[] {
    return asArray(signalSnapshot?.[key])
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            name: asString(row.category) ?? "Untitled",
            count: asNumber(row.count) ?? 0,
            eddyCount: asNumber(row.eddyCount),
            eddyRate: asNumber(row.eddyRate),
            changePercent: asNumber(row.changePercent),
        }))
        .sort((a, b) => b.count - a.count);
}

export type DigestModelTrend = {
    name: string;
    count: number;
    eddyCount?: number;
    eddyRate?: number;
};

/** `signalSnapshot.modelTrends` — per-model volume/eddy, no changePercent. */
export function getModelTrends(
    signalSnapshot: Record<string, unknown> | undefined,
): DigestModelTrend[] {
    return asArray(signalSnapshot?.modelTrends)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            name: asString(row.modelName) ?? "Untitled model",
            count: asNumber(row.count) ?? 0,
            eddyCount: asNumber(row.eddyCount),
            eddyRate: asNumber(row.eddyRate),
        }))
        .sort((a, b) => b.count - a.count);
}

export type DigestTrendingTopic = {
    name: string;
    volume: number;
    share?: number;
    changePercent?: number;
    isNew?: boolean;
    isToZero?: boolean;
};

function mapTrendingTopics(rows: unknown[]): DigestTrendingTopic[] {
    return rows
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            name: asString(row.topicName) ?? "Untitled topic",
            volume: asNumber(row.volume) ?? 0,
            share: asNumber(row.share),
            changePercent: asNumber(row.changePercent),
            isNew: row.isNew === true,
            isToZero: row.isToZero === true,
        }));
}

/** `signalSnapshot.topicMomentum` — trending topics and the subset newly on the rise. */
export function getTopicMomentum(
    signalSnapshot: Record<string, unknown> | undefined,
): {
    trending: DigestTrendingTopic[];
    onTheRise: DigestTrendingTopic[];
} {
    const momentum = asRecord(signalSnapshot?.topicMomentum);
    return {
        trending: mapTrendingTopics(asArray(momentum?.trendingTopics)),
        onTheRise: mapTrendingTopics(asArray(momentum?.topicsOnTheRise)),
    };
}

export type DigestRiskTrendRow = {
    bucket: string;
    count: number;
    totalCount: number;
    prevalenceRate?: number;
    eddyRate?: number;
    changePercent?: number;
};

function mapRiskTrend(rows: unknown[]): DigestRiskTrendRow[] {
    return rows
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            bucket: asString(row.bucket) ?? "—",
            count: asNumber(row.count) ?? 0,
            totalCount: asNumber(row.totalCount) ?? 0,
            prevalenceRate: asNumber(row.prevalenceRate),
            eddyRate: asNumber(row.eddyRate),
            changePercent: asNumber(row.changePercent),
        }));
}

/** `signalSnapshot.riskSignals` — safety-event and HIPAA-compliance time series. */
export function getRiskSignals(
    signalSnapshot: Record<string, unknown> | undefined,
): {
    safetyEvents: DigestRiskTrendRow[];
    hipaaCompliance: DigestRiskTrendRow[];
} {
    const risk = asRecord(signalSnapshot?.riskSignals);
    return {
        safetyEvents: mapRiskTrend(asArray(risk?.safetyEventsOverTime)),
        hipaaCompliance: mapRiskTrend(asArray(risk?.hipaaComplianceOverTime)),
    };
}

export type DigestModelResultShare = {
    label: string;
    count: number;
    share?: number;
};
export type DigestModelDistribution = {
    modelName: string;
    results: DigestModelResultShare[];
};

/** `signalSnapshot.modelDistributions` — per-model result-value breakdown (e.g. Meets/Does Not Meet). */
export function getModelDistributions(
    signalSnapshot: Record<string, unknown> | undefined,
): DigestModelDistribution[] {
    return asArray(signalSnapshot?.modelDistributions)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            modelName: asString(row.modelName) ?? "Untitled model",
            results: asArray(row.results)
                .map((r) => asRecord(r))
                .filter((r): r is Record<string, unknown> => !!r)
                .map((r) => ({
                    label:
                        asString(r.label) ??
                        asString(r.value) ??
                        asString(r.result) ??
                        "—",
                    count: asNumber(r.count) ?? 0,
                    share: asNumber(r.share),
                })),
        }));
}

/** `signalSnapshot.volumeOverTime` — daily conversation counts across the window. */
export function getVolumeOverTime(
    signalSnapshot: Record<string, unknown> | undefined,
): Array<{ bucket: string; count: number }> {
    return asArray(signalSnapshot?.volumeOverTime)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            bucket: asString(row.bucket) ?? "—",
            count: asNumber(row.count) ?? 0,
        }));
}

export type DigestClassifier = {
    id?: string;
    name: string;
    type?: string;
    family?: string;
    purpose?: string;
    outputs: string[];
};

/** `classifierCatalog.classifiers` — the full model/classifier library available to this tenant. */
export function getClassifierList(
    classifierCatalog: Record<string, unknown> | undefined,
): DigestClassifier[] {
    return asArray(classifierCatalog?.classifiers)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            id: asString(row.id),
            name: asString(row.name) ?? "Untitled classifier",
            type: asString(row.type),
            family: asString(row.family),
            purpose: asString(row.purpose),
            outputs: asArray(row.outputs)
                .map((o) => asString(o))
                .filter((o): o is string => !!o),
        }));
}

export type DigestPermission = {
    id?: string;
    domain?: string;
    action?: string;
    resource?: string;
    description?: string;
};

/** Ultra-user flag + the full effective-permission list from the `permissions` context section. */
export function getPermissionsSummary(
    permissions: Record<string, unknown> | undefined,
): { isUltraUser: boolean; effectivePermissions: DigestPermission[] } {
    return {
        isUltraUser: permissions?.isUltraUser === true,
        effectivePermissions: asArray(permissions?.effectivePermissions)
            .map((row) => asRecord(row))
            .filter((row): row is Record<string, unknown> => !!row)
            .map((row) => ({
                id: asString(row.id),
                domain: asString(row.domain),
                action: asString(row.action),
                resource: asString(row.resource),
                description: asString(row.description),
            })),
    };
}

export type DigestHierarchy = { id?: string; name: string; path?: string };

/** `hierarchies.visibleHierarchies` — the flattened hierarchy levels visible to this user. */
export function getHierarchyList(
    hierarchies: Record<string, unknown> | undefined,
): DigestHierarchy[] {
    return asArray(hierarchies?.visibleHierarchies)
        .map((row) => asRecord(row))
        .filter((row): row is Record<string, unknown> => !!row)
        .map((row) => ({
            id: asString(row.id),
            name: asString(row.name) ?? "Untitled",
            path: asString(row.path),
        }));
}
